import { createEffect} from '@ngrx/effects';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import * as AuthActions from './auth.actions';
import { catchError, map, of, tap, mergeMap, exhaustMap, switchMap, withLatestFrom } from 'rxjs';
import { inject } from '@angular/core';
import { AuthTokenService } from '../../services/auth-token.service';
import { loadUser } from '../user/user.actions';
import * as UserActions from '../user/user.actions';
import * as IngredientActions from '../ingredient/ingredient.actions';
import * as StorageActions from '../storage/storage.actions';
import * as HouseholdActions from '../household/household.actions';
import * as ShoppingListActions from '../shopping-list/shopping-list.actions';
import * as LeftoverActions from '../leftover/leftover.actions';
import * as RecipeActions from '../recipe/recipe.actions';
import * as MealPlannerActions from '../meal-planner/meal-planner.actions';
import * as FeedbackActions from '../feedback/feedback.actions';
import * as AnnouncementActions from '../announcement/announcement.actions'
import * as VersionActions from '../version/version.actions';
import * as ImageActions from '../image/image.actions';
import { Action, Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { LogoutType } from '../../../shared/enums/logoutType.enum';
import { TranslateService } from '@ngx-translate/core';
import { navigateAfterAuth } from '../../../shared/utils/auth-redirect.utils';
import { selectIsLoggedIn } from './auth.selectors';
import { selectUserId } from '../user/user.selectors';


export const login$ = createEffect(
  (
    actions$ = inject(Actions),
    authTokenService = inject(AuthTokenService),
    notification = inject(NotificationService),
    translate = inject(TranslateService),
    router = inject(Router)
  ) =>
    actions$.pipe(
      ofType(AuthActions.login),
      mergeMap((action) =>
        authTokenService.login(action.emailOrUsername, action.password).pipe(
          map((response) =>
            AuthActions.loginSuccess({ token: response.token, userId: response.userId })
          ),
          catchError((err: any) => {
            const status = err?.status;
            const body = err?.error;
            const code =
              body?.code ??
              body?.error?.code ??
              body?.error_code ??
              undefined;

            if (status === 403 && code === 'EMAIL_NOT_VERIFIED') {
              notification.info(translate.instant('auth.notifications.emailNotVerified'));
              router.navigate(['/verify-email-sent']);
              return of(AuthActions.loginEmailNotVerified());
            }

            if (status === 403 && code === 'WEB_PORTAL_ACCESS_DENIED') {
              authTokenService.clearToken();
              notification.info(translate.instant('auth.notifications.webPortalAccessDenied'));
              router.navigate(['/download']);
              return of(AuthActions.loginFailure({ error: err }));
            }

            notification.error(translate.instant('auth.notifications.loginFailure'));
            return of(AuthActions.loginFailure({ error: err }));
          })
        )
      )
    ),
  { functional: true }
);

export const loginSuccess$ = createEffect(
  (actions$ = inject(Actions), authTokenService = inject(AuthTokenService), router = inject(Router)) =>
    actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(({ token }) => authTokenService.storeToken(token)), // Store token
      map(({ userId }) => loadUser({ userId })), // Dispatch action to load user
      tap(() => navigateAfterAuth(router, authTokenService))
    ),
  { functional: true }
);

export const logout$ = createEffect(
  (
    actions$ = inject(Actions),
    router = inject(Router),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AuthActions.logout),
      tap(({ source = LogoutType.Manual }) => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('unitSystem');
        localStorage.removeItem('dietary');
        router.navigate(['/login']);

        // Handle notification based on logout source
        switch (source) {
          case LogoutType.Manual:
            notification.success(translate.instant('auth.notifications.logoutSuccess'));
            break;
          case LogoutType.Timeout:
            notification.info(translate.instant('auth.notifications.sessionExpired'));
            break;
          // Skip notification for Deletion
        }
      }),
      mergeMap(() => [
        UserActions.clearUser(),
        IngredientActions.resetIngredients(),
        StorageActions.resetStorage(),
        ShoppingListActions.resetShoppingList(),
        HouseholdActions.clearHouseholds(),
        LeftoverActions.resetLeftovers(),
        RecipeActions.clearAllRecipes(),
        MealPlannerActions.resetMealPlanner(),
        FeedbackActions.resetFeedbacks(),
        AnnouncementActions.resetAnnouncements(),
        VersionActions.resetBackendVersion(),
        ImageActions.resetImageState(),
      ])
    ),
  { functional: true }
);

export const silentLogout$ = createEffect(() =>
  inject(Actions).pipe(
    ofType(AuthActions.silentClearSession),
    tap(() => {
      localStorage.removeItem('authToken');
      localStorage.removeItem('unitSystem');
      localStorage.removeItem('dietary');
    }),
    map(() => [
      UserActions.clearUser(),
      IngredientActions.resetIngredients(),
      StorageActions.resetStorage(),
      ShoppingListActions.resetShoppingList(),
      HouseholdActions.clearHouseholds(),
      LeftoverActions.resetLeftovers(),
      RecipeActions.clearAllRecipes(),
      MealPlannerActions.resetMealPlanner(),
      FeedbackActions.resetFeedbacks(),
      AnnouncementActions.resetAnnouncements(),
      VersionActions.resetBackendVersion(),
      ImageActions.resetImageState(),
    ]),
    mergeMap(actions => actions)
  ),
  { functional: true }
);

export const register$ = createEffect(
  (actions$ = inject(Actions),
   authTokenService = inject(AuthTokenService),
   notification = inject(NotificationService),
   translate = inject(TranslateService)) =>
    actions$.pipe(
      ofType(AuthActions.register),
      exhaustMap((action) =>
        authTokenService.register(action.username, action.email, action.password, action.preferred_language, action.preferred_timezone).pipe(
          map((response) =>
            AuthActions.registerSuccess({ token: response.token, userId: response.userId })
          ),
          catchError((error) => {
            if (error?.status === 409) {
              notification.error(translate.instant('auth.notifications.duplicate'));
            } else {
              notification.error(translate.instant('auth.notifications.registrationFailure'));
            }
            return of(AuthActions.registerFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const registerSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(AuthActions.registerSuccess),
      tap(() => router.navigate(['/verify-email-sent']))
    ),
  { functional: true, dispatch: false }
);

// call backend with token
export const verifyEmail$ = createEffect(
  (actions$ = inject(Actions), auth = inject(AuthTokenService)) =>
    actions$.pipe(
      ofType(AuthActions.verifyEmail),
      switchMap(({ token }) =>
        auth.verifyEmail(token).pipe(
          map(() => AuthActions.verifyEmailSuccess()),
          catchError((err) => {
            const msg = err?.error?.message || 'verification_failed';
            return of(AuthActions.verifyEmailFailure({ error: msg }));
          })
        )
      )
    ),
  { functional: true }
);

export const resendVerificationEmail$ = createEffect(
  (actions$ = inject(Actions), auth = inject(AuthTokenService)) =>
    actions$.pipe(
      ofType(AuthActions.resendVerificationEmail),
      switchMap(({ email }) =>
        auth.resendVerificationEmail(email).pipe(
          map(() => AuthActions.resendVerificationEmailSuccess()),
          catchError(err => {
            const msg = err?.error?.message || 'resend_failed';
            return of(AuthActions.resendVerificationEmailFailure({ error: msg }));
          })
        )
      )
    ),
  { functional: true }
);

export const requestEmailChange$ = createEffect(
  (
    actions$ = inject(Actions),
    svc = inject(AuthTokenService),
    notification = inject(NotificationService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(AuthActions.requestEmailChange),
      mergeMap(({ newEmail }) =>
        svc.requestEmailChange(newEmail).pipe(
          map(() => AuthActions.requestEmailChangeSuccess({ pendingEmail: newEmail })),
          tap(() => notification.info(translate.instant('profile.emailChange.sent'))),
          catchError((err) => {
            const msg =
              err?.status === 409
                ? translate.instant('profile.emailChange.conflict')
                : translate.instant('profile.emailChange.failed');
            notification.error(msg);
            return of(AuthActions.requestEmailChangeFailure({ error: msg }));
          })
        )
      )
    ),
  { functional: true }
);

export const confirmEmailChange$ = createEffect(
  (
    actions$ = inject(Actions),
    svc = inject(AuthTokenService),
    notification = inject(NotificationService),
    translate = inject(TranslateService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(AuthActions.confirmEmailChange),
      mergeMap(({ token }) =>
        svc.confirmEmailChange(token).pipe(
          // map to a simple value so we can combine with store selectors
          map(() => true),
          withLatestFrom(
            store.select(selectIsLoggedIn),
            store.select(selectUserId) // <- grab userId from state
          ),
          tap(() => {
            notification.success(translate.instant('profile.emailChange.confirmed'));
          }),
          // dispatch success + optionally loadUser({ userId })
          switchMap(([_, isLoggedIn, userId]) => {
            const next: Action[] = [AuthActions.confirmEmailChangeSuccess()];
            if (isLoggedIn && userId) {
              next.push(UserActions.loadUser({ userId }));
            }
            return of(...next);
          }),
          catchError((err) => {
            const msg = translate.instant('profile.emailChange.confirmFailed');
            notification.error(msg);
            return of(AuthActions.confirmEmailChangeFailure({ error: msg }));
          })
        )
      )
    ),
  { functional: true }
);

export const requestPasswordReset$ = createEffect(
  (
    actions$ = inject(Actions),
    auth = inject(AuthTokenService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AuthActions.requestPasswordReset),
      mergeMap(({ email }) =>
        auth.requestPasswordReset(email).pipe(
          map(() => AuthActions.requestPasswordResetSuccess()),
          tap(() =>
            notification.info(translate.instant('password.forgot.sent')) // generic success
          ),
          catchError(() =>
            of(AuthActions.requestPasswordResetFailure({
              error: translate.instant('password.forgot.failed')
            }))
          )
        )
      )
    ),
  { functional: true }
);

export const resetPassword$ = createEffect(
  (
    actions$ = inject(Actions),
    auth = inject(AuthTokenService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AuthActions.resetPassword),
      mergeMap(({ token, newPassword }) =>
        auth.resetPassword(token, newPassword).pipe(
          map(() => AuthActions.resetPasswordSuccess()),
          tap(() => {
            notification.success(translate.instant('password.reset.success'));
          }),
          catchError(() =>
            of(AuthActions.resetPasswordFailure({
              error: translate.instant('password.reset.failed')
            }))
          )
        )
      )
    ),
  { functional: true }
);

export const logoutBackend$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthTokenService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AuthActions.logoutBackend),
      exhaustMap(() =>
        authService.logoutBackend().pipe(
          map(() => AuthActions.logoutBackendSuccess()),
          catchError((error) => {
            notification.error(translate.instant('auth.notifications.backendLogoutFailure'));
            return of(AuthActions.logoutBackendFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

// Chain logout and cleanup like before
export const logoutBackendSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) =>
    actions$.pipe(
      ofType(AuthActions.logoutBackendSuccess),
      tap(() => {
        store.dispatch(AuthActions.logout({ source: LogoutType.Manual })); // Clears store and triggers token cleanup + navigation
      })
    ),
  { functional: true, dispatch: false }
);

export const googleLogin$ = createEffect(
  (
    actions$ = inject(Actions),
    authTokenService = inject(AuthTokenService),
    notification = inject(NotificationService),
    translate = inject(TranslateService),
    router = inject(Router)
  ) =>
    actions$.pipe(
      ofType(AuthActions.googleLogin),
      exhaustMap(({ credential, preferred_timezone }) =>
        authTokenService.googleLogin(credential, preferred_timezone).pipe(
          map((response) =>
            AuthActions.loginSuccess({ token: response.token, userId: response.userId })
          ),
          catchError((err: any) => {
            if (err?.status === 403 && err?.error?.code === 'WEB_PORTAL_ACCESS_DENIED') {
              authTokenService.clearToken();
              notification.info(translate.instant('auth.notifications.webPortalAccessDenied'));
              router.navigate(['/download']);
              return of(AuthActions.googleLoginFailure({ error: err }));
            }

            notification.error(translate.instant('auth.notifications.loginFailure'));
            return of(AuthActions.googleLoginFailure({ error: err }));
          })
        )
      )
    ),
  { functional: true }
);


export const AuthEffects = {
  login$,
  googleLogin$,
  loginSuccess$,
  logout$,
  register$,
  registerSuccess$,
  logoutBackend$,
  logoutBackendSuccess$,
  verifyEmail$,
  resendVerificationEmail$,
  requestEmailChange$,
  confirmEmailChange$,
  requestPasswordReset$,
  resetPassword$
};
